import { DefaultQueryParams } from "@gtmhub/configuration/workday/models";
import { MISSING_SESSION_ID_STATE_PARAM } from "@gtmhub/goals/models";
import { GlobalSearchItemReturnState, IGlobalMultiSearchItems } from "@gtmhub/search/models/global-search";
import { SearchConditionSettings, compoundSuffix } from "@gtmhub/search/services/search.service";
import { buildSearchConditions } from "@gtmhub/shared/utils/search-utils";
import { ISearchRequestsBody } from "@webapp/search/models/search-api.models";
import {
  GlobalSearchResult,
  ITEMS_SHOWN_IF_EVERYWHERE,
  ITEMS_SHOWN_IF_NOT_EVERYWHERE,
  Search,
  SearchCollection,
  SearchFacetsOptions,
  SearchFacetsOptionsEnum,
  SearchFacetsOptionsMap,
  SearchMultiDTO,
  fieldsToSearchByMap,
  responseFieldsMap,
  searchCollectionsTranslationMap,
} from "@webapp/search/models/search.models";

const getSearchFields = (searchCollection: string[]): { name: string }[] => {
  return searchCollection.map((field) => {
    return {
      name: field,
    };
  });
};

const getSearchBodyForSingleCollection = (facet: string, settings: SearchConditionSettings): ISearchRequestsBody[] => {
  const isCompound = searchCollectionsTranslationMap[facet].length > 1;

  return searchCollectionsTranslationMap[facet].map((collection) => {
    const collectionKey = isCompound ? collection + compoundSuffix : collection;
    return {
      collectionName: collection,
      searchFields: getSearchFields(fieldsToSearchByMap[collectionKey]),
      responseFields: responseFieldsMap[collectionKey],
      searchConditions: buildSearchConditions(collection, settings),
    };
  });
};

export const getSearchBodyV2 = (searchTerm: string, facets: string[], settings: SearchConditionSettings = {}): SearchMultiDTO => {
  const searchFields = {} as Record<string, ISearchRequestsBody[]>;

  facets.forEach((f) => {
    searchFields[f] = getSearchBodyForSingleCollection(f, settings);
  });
  return {
    searchTerm: searchTerm,
    operator: 0,
    searchRequests: searchFields,
  };
};

export const convertToOrderedArray = (searchResults: IGlobalMultiSearchItems): GlobalSearchResult[] => {
  const result: GlobalSearchResult[] = [] as GlobalSearchResult[];
  Object.keys(searchResults.items).forEach((key: SearchFacetsOptionsEnum) => {
    if (searchResults.items[key].items.length > 0) {
      result.push({ ...searchResults.items[key], collectionName: key });
    }
  });
  return result.sort((a, b) => b.maxScore - a.maxScore);
};

export const getIcon = (collectionName: SearchCollection): string => {
  let icon = "";
  switch (collectionName) {
    case "sessions":
    case "goals":
    case "metrics":
    case "tasks":
    case "automations":
    case "whiteboards":
    case "lists":
    case "insights":
    case "filters":
    case "kpis": {
      icon = collectionName.slice(0, collectionName.length - 1);
      break;
    }
    case "dashboards": {
      icon = "insightboard";
    }
  }
  return icon;
};

export const getItemReturnState = (collectionName: SearchCollection, item: Search<SearchCollection>): GlobalSearchItemReturnState => {
  switch (collectionName) {
    case "sessions": {
      return {
        state: "gtmhub.goals.all",
        stateParams: { planningSessionId: item.id, page: 1 },
      };
    }
    case "goals": {
      const sessionId = item.fields["sessionId"] || MISSING_SESSION_ID_STATE_PARAM;

      return {
        state: "gtmhub.goals.all._goalSidebar.goal",
        stateParams: { id: item.id, planningSessionId: sessionId },
      };
    }
    case "metrics": {
      const sessionId = item.fields["sessionId"] || MISSING_SESSION_ID_STATE_PARAM;

      return {
        state: "gtmhub.goals.all._goalSidebar.goal._metric.metric",
        stateParams: { id: item.fields["goalId"], planningSessionId: sessionId, metricId: item.id },
      };
    }
    case "filters": {
      return {
        state: "gtmhub.okrView",
        stateParams: { okrViewId: item.id },
      };
    }
    case "tasks": {
      return {
        state: "gtmhub.home.dashboard.task",
        stateParams: { taskId: item.id },
      };
    }
    case "lists": {
      return {
        state: "gtmhub.list",
        stateParams: { listId: item.id, isNewList: false },
      };
    }
    case "automations": {
      return {
        state: "gtmhub.automation.rules",
        stateParams: { automationId: item.id },
      };
    }
    case "teams": {
      return {
        state: "gtmhub.teams.team",
        stateParams: { teamId: item.id },
      };
    }
    case "users": {
      return {
        state: "gtmhub.employees.employee",
        stateParams: { employeeId: item.id },
      };
    }
    case "kpis": {
      return {
        state: "gtmhub.kpis.details",
        stateParams: { kpiId: item.id },
      };
    }
    case "dashboards": {
      return {
        state: "gtmhub.insightboard",
        stateParams: { dashboardId: item.id },
      };
    }
    case "whiteboards": {
      return {
        state: "gtmhub.whiteboard",
        stateParams: { whiteboardId: item.id },
      };
    }
  }
};

export const getFacetsAndQueryParams = (currentFacet: string, facetOptionsMap: SearchFacetsOptionsMap): { facets: string[]; queryParams: DefaultQueryParams } => {
  let facets: SearchFacetsOptions[];
  const queryParams: DefaultQueryParams = { skip: 0, take: ITEMS_SHOWN_IF_EVERYWHERE };

  if (currentFacet === SearchFacetsOptionsEnum.Everywhere) {
    facets = Object.values(facetOptionsMap).filter((f) => f !== SearchFacetsOptionsEnum.Everywhere);
    queryParams.take = ITEMS_SHOWN_IF_EVERYWHERE;
  } else {
    facets = [facetOptionsMap[currentFacet]];
    queryParams.take = ITEMS_SHOWN_IF_NOT_EVERYWHERE;
  }
  return { facets, queryParams };
};
